import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { inputStyle } from '../lib/ui.js';

export function TemplatesModal({ location, weekStart, currentUser, shiftCount, onClose, onApplied }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', `/api/shift-templates?location_id=${location.id}`, null, headers);
      setTemplates(r.templates || []);
    } catch (e) { setMsg(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function saveTemplate() {
    if (!name.trim()) { setMsg('Name required'); return; }
    setBusy(true); setMsg('');
    try {
      const r = await api('POST', '/api/shift-templates',
        { location_id: location.id, name, note, week_start: weekStart }, headers);
      setMsg(`Saved template with ${r.items} shift${r.items > 1 ? 's' : ''}.`);
      setName(''); setNote(''); load();
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      setMsg(`Save failed: ${m}`);
    }
    finally { setBusy(false); }
  }

  async function applyTemplate(tplId) {
    if (!confirm(`Apply template to week of ${weekStart}? Existing shifts won't be touched; only new draft shifts will be added.`)) return;
    setBusy(true); setMsg('');
    try {
      const r = await api('POST', `/api/shift-templates/${tplId}/apply`,
        { week_start: weekStart }, headers);
      setMsg(`Applied: ${r.created} new draft${r.created !== 1 ? 's' : ''}, ${r.skipped} skipped.`);
      setTimeout(() => onApplied(), 800);
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      setMsg(`Apply failed: ${m}`);
    }
    finally { setBusy(false); }
  }

  async function removeTemplate(tplId) {
    if (!confirm('Delete this template? Cannot be undone.')) return;
    try {
      await api('DELETE', `/api/shift-templates/${tplId}`, null, headers);
      load();
    } catch (e) { setMsg(e.message || 'Delete failed'); }
  }

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Schedule templates · {location.name}</div>
          <button onClick={onClose} className="btn-secondary" style={{ fontSize: 12 }}>Close</button>
        </div>

        {/* Save current week as template */}
        <div style={{ padding: 12, background: '#fafbfc', border: '1px solid #d0d4d9', borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Save week of {weekStart} as template ({shiftCount} shift{shiftCount !== 1 ? 's' : ''})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Template name (e.g. Standard week)" style={inputStyle} />
            <input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)" style={inputStyle} />
          </div>
          <button onClick={saveTemplate} disabled={busy || shiftCount === 0}
            style={{ marginTop: 8, background: 'var(--forest)', color: '#fff', border: 'none',
                      borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', opacity: (busy || shiftCount === 0) ? 0.5 : 1 }}>
            Save as template
          </button>
        </div>

        {msg && <div style={{ fontSize: 12, color: '#37474f', marginBottom: 8 }}>{msg}</div>}

        <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
          Saved templates
        </div>
        {loading && <div style={{ fontSize: 12, color: '#9e9e9e' }}>Loading…</div>}
        {!loading && templates.length === 0 && (
          <div style={{ fontSize: 12, color: '#9e9e9e', fontStyle: 'italic' }}>None yet. Save the current week above to start.</div>
        )}
        {templates.map((t) => (
          <div key={t.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: '#757575' }}>
                {t.item_count} shift{t.item_count !== 1 ? 's' : ''} · saved {t.created_at?.slice(0, 10)} by {t.created_by_name || 'unknown'}
                {t.note ? ` · ${t.note}` : ''}
              </div>
            </div>
            <button onClick={() => applyTemplate(t.id)} disabled={busy}
              style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600,
                        background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Apply to week
            </button>
            <button onClick={() => removeTemplate(t.id)}
              style={{ padding: '4px 8px', fontSize: 11, background: '#fff', color: '#c62828',
                        border: '1px solid #ffcdd2', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
